import React, { useState, useEffect } from 'react';
import { 
  BuildingOfficeIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon, 
  UsersIcon, 
  TruckIcon,
  DocumentTextIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { companiesAPI } from '../services/api'; // Import from api.js instead of axios

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalDrivers: 0,
    totalVehicles: 0,
    activeCompanies: 0
  });
  
  // Edit state
  const [editingCompany, setEditingCompany] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    registrationNumber: '',
    taxId: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(null);

  // Color constants - Using your primary and secondary colors
  const PRIMARY_COLOR = '#e28743'; // Orange
  const SECONDARY_COLOR = '#304a3f'; // Dark Green/Teal

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use companiesAPI from api.js
      const response = await companiesAPI.getAll();
      
      // Handle different response structures
      let companiesData = [];
      if (response && response.success) {
        companiesData = response.data || [];
      } else if (Array.isArray(response)) {
        companiesData = response;
      } else if (response && Array.isArray(response.data)) {
        companiesData = response.data;
      }
      
      setCompanies(companiesData);
      
      const totalDrivers = companiesData.reduce((sum, company) => sum + (company.drivers?.length || 0), 0);
      const totalVehicles = companiesData.reduce((sum, company) => sum + (company.vehicles?.length || 0), 0);
      const activeCompanies = companiesData.filter(c => c.status === 'active').length;
      
      setStats({
        totalCompanies: companiesData.length,
        totalDrivers,
        totalVehicles,
        activeCompanies
      });
    } catch (err) {
      console.error('Error fetching companies:', err);
      
      let errorMessage = 'Unable to load companies. Please try again.';
      
      if (err.status === 401) {
        errorMessage = 'Authentication required. Please login again.';
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.error?.message) {
        errorMessage = err.error.message;
      }
      
      setError(errorMessage);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  // Start editing a company
  const handleEditStart = (company) => {
    setEditingCompany(company._id);
    setEditForm({
      name: company.name || '',
      email: company.email || '',
      phone: company.phone || '',
      registrationNumber: company.registrationNumber || '',
      taxId: company.taxId || ''
    });
    setUpdateError(null);
    setUpdateSuccess(null);
  };

  // Cancel editing
  const handleEditCancel = () => {
    setEditingCompany(null);
    setEditForm({
      name: '',
      email: '',
      phone: '',
      registrationNumber: '',
      taxId: ''
    });
    setUpdateError(null);
    setUpdateSuccess(null);
  };

  // Handle form input changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Update company
  const handleUpdateCompany = async (companyId) => {
    if (!companyId) return;
    
    // Validate required fields
    if (!editForm.name.trim()) {
      setUpdateError('Company name is required');
      return;
    }

    if (!editForm.email.trim()) {
      setUpdateError('Email is required');
      return;
    }

    try {
      setIsUpdating(true);
      setUpdateError(null);
      setUpdateSuccess(null);
      
      const updateData = {
        name: editForm.name,
        phone: editForm.phone,
        email: editForm.email,
        registrationNumber: editForm.registrationNumber,
        taxId: editForm.taxId
      };

      // Use companiesAPI from api.js
      const response = await companiesAPI.update(companyId, updateData);
      
      if (response && response.success) {
        // Update the company in the state
        setCompanies(prevCompanies => 
          prevCompanies.map(company => 
            company._id === companyId 
              ? { ...company, ...updateData } 
              : company
          )
        );
        
        setUpdateSuccess('Company updated successfully!');
        
        // Clear edit mode after a delay
        setTimeout(() => {
          setEditingCompany(null);
          setUpdateSuccess(null);
        }, 2000);
      } else {
        setUpdateError(response?.message || 'Failed to update company');
      }
    } catch (err) {
      console.error('Error updating company:', err);
      
      let errorMessage = 'Failed to update company. Please try again.';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.error?.message) {
        errorMessage = err.error.message;
      }
      
      setUpdateError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete company
  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete "${companyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Note: You might need to add a delete method to your companiesAPI
      // For now, we'll just update the UI
      console.log(`Would delete company ${companyId}`);
      
      // Remove company from state
      setCompanies(prevCompanies => prevCompanies.filter(company => company._id !== companyId));
      
      // Update stats
      setStats(prev => ({
        ...prev,
        totalCompanies: prev.totalCompanies - 1
      }));
      
      alert('Company deleted successfully!');
      
      // If you want to actually delete from backend, add this to your api.js:
      // delete: (id) => api.delete(`/companies/${id}`)
      // Then uncomment this:
      // const response = await companiesAPI.delete(companyId);
      // if (response && response.success) {
      //   // Update UI as above
      // }
      
    } catch (err) {
      console.error('Error deleting company:', err);
      alert('Failed to delete company. Please try again.');
    }
  };

  const toggleExpand = (companyId) => {
    setExpandedCompany(expandedCompany === companyId ? null : companyId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDriverStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'available': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVehicleStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status.toLowerCase()) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'idle': return 'bg-yellow-100 text-yellow-800';
      case 'maintenance': return 'bg-red-100 text-red-800';
      case 'on_trip': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAddressString = (address) => {
    if (!address) return 'No address provided';
    const parts = [
      address.street,
      address.city,
      address.state,
      address.country
    ].filter(part => part && part.trim());
    
    return parts.length > 0 ? parts.join(', ') : 'No address provided';
  };

  const filteredCompanies = companies.filter(company => {
    if (!company) return false;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (company.name || '').toLowerCase().includes(searchLower) ||
      (company.email || '').toLowerCase().includes(searchLower) ||
      (company.registrationNumber || '').toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: SECONDARY_COLOR }}>
                Companies Management
              </h1>
              <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">
                Manage all registered companies in the system
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search companies..."
                  className="pl-9 md:pl-10 pr-3 md:pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent w-full text-sm md:text-base"
                  style={{ 
                    focusRingColor: PRIMARY_COLOR,
                    '&:focus': {
                      ringColor: PRIMARY_COLOR,
                      borderColor: PRIMARY_COLOR
                    }
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 md:mb-6 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start md:items-center">
              <ExclamationTriangleIcon className="w-4 h-4 md:w-5 md:h-5 text-red-600 mr-2 mt-0.5 md:mt-0 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-600 font-medium text-sm md:text-base">Error loading companies</p>
                <p className="text-red-500 text-xs md:text-sm mt-1">{error}</p>
                <button
                  onClick={fetchCompanies}
                  className="mt-2 text-xs md:text-sm text-red-600 hover:text-red-800 underline"
                >
                  Click to retry
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Update Status Messages */}
        {updateError && (
          <div className="mb-4 md:mb-6 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-4 h-4 md:w-5 md:h-5 text-red-600 mr-2 flex-shrink-0" />
              <p className="text-red-600 text-sm md:text-base">{updateError}</p>
            </div>
          </div>
        )}

        {updateSuccess && (
          <div className="mb-4 md:mb-6 p-3 md:p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-600 mr-2 flex-shrink-0" />
              <p className="text-green-600 text-sm md:text-base">{updateSuccess}</p>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white rounded-lg shadow p-4 md:p-6">
            <div className="flex items-center">
              <div className="p-2 md:p-3 rounded-full" style={{ backgroundColor: `${PRIMARY_COLOR}20` }}>
                <BuildingOfficeIcon className="w-5 h-5 md:w-6 md:h-6" style={{ color: PRIMARY_COLOR }} />
              </div>
              <div className="ml-3 md:ml-4">
                <p className="text-xs md:text-sm text-gray-500">Total Companies</p>
                <p className="text-xl md:text-2xl font-bold" style={{ color: SECONDARY_COLOR }}>
                  {stats.totalCompanies}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 md:p-6">
            <div className="flex items-center">
              <div className="p-2 md:p-3 rounded-full" style={{ backgroundColor: `${SECONDARY_COLOR}20` }}>
                <UsersIcon className="w-5 h-5 md:w-6 md:h-6" style={{ color: SECONDARY_COLOR }} />
              </div>
              <div className="ml-3 md:ml-4">
                <p className="text-xs md:text-sm text-gray-500">Total Drivers</p>
                <p className="text-xl md:text-2xl font-bold" style={{ color: SECONDARY_COLOR }}>
                  {stats.totalDrivers}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 md:p-6">
            <div className="flex items-center">
              <div className="p-2 md:p-3 rounded-full" style={{ backgroundColor: `${PRIMARY_COLOR}20` }}>
                <TruckIcon className="w-5 h-5 md:w-6 md:h-6" style={{ color: PRIMARY_COLOR }} />
              </div>
              <div className="ml-3 md:ml-4">
                <p className="text-xs md:text-sm text-gray-500">Total Vehicles</p>
                <p className="text-xl md:text-2xl font-bold" style={{ color: SECONDARY_COLOR }}>
                  {stats.totalVehicles}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 md:p-6">
            <div className="flex items-center">
              <div className="p-2 md:p-3 rounded-full" style={{ backgroundColor: `${SECONDARY_COLOR}20` }}>
                <ExclamationTriangleIcon className="w-5 h-5 md:w-6 md:h-6" style={{ color: SECONDARY_COLOR }} />
              </div>
              <div className="ml-3 md:ml-4">
                <p className="text-xs md:text-sm text-gray-500">Active Companies</p>
                <p className="text-xl md:text-2xl font-bold" style={{ color: SECONDARY_COLOR }}>
                  {stats.activeCompanies}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Companies List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-3 md:px-6 py-3 md:py-4 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-4">
              <h2 className="text-lg md:text-xl font-semibold" style={{ color: SECONDARY_COLOR }}>
                All Companies ({filteredCompanies.length})
              </h2>
              <div className="text-xs md:text-sm text-gray-600">
                Showing {filteredCompanies.length} of {companies.length} companies
              </div>
            </div>
          </div>

          {error && companies.length === 0 ? (
            <div className="p-6 md:p-8 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 md:w-16 md:h-16 text-red-300 mx-auto" />
              <p className="mt-3 md:mt-4 text-red-500 text-sm md:text-base">Failed to load companies</p>
              <button
                onClick={fetchCompanies}
                className="mt-3 md:mt-4 px-4 py-2 rounded-lg transition-colors font-medium text-sm md:text-base"
                style={{ 
                  backgroundColor: PRIMARY_COLOR,
                  color: 'white'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d1763a'}
                onMouseLeave={(e) => e.target.style.backgroundColor = PRIMARY_COLOR}
              >
                Retry Loading
              </button>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="p-6 md:p-8 text-center">
              <BuildingOfficeIcon className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto" />
              <p className="mt-3 md:mt-4 text-gray-500 text-sm md:text-base">No companies found</p>
              {searchTerm && (
                <p className="text-xs md:text-sm text-gray-400 mt-2">Try adjusting your search</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredCompanies.map((company) => (
                <div key={company?._id || Math.random()} className="p-3 md:p-6 hover:bg-gray-50 transition-colors">
                  {/* Company Header with Edit Mode */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 md:gap-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: `${PRIMARY_COLOR}20` }}>
                          <BuildingOfficeIcon className="w-5 h-5 md:w-6 md:h-6" style={{ color: PRIMARY_COLOR }} />
                        </div>
                        <div className="flex-1">
                          {editingCompany === company?._id ? (
                            <div className="space-y-2 md:space-y-3">
                              <div>
                                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                  Company Name *
                                </label>
                                <input
                                  type="text"
                                  name="name"
                                  value={editForm.name}
                                  onChange={handleEditChange}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm md:text-base focus:ring-2 focus:border-transparent"
                                  style={{ focusRingColor: PRIMARY_COLOR }}
                                  placeholder="Enter company name"
                                />
                              </div>
                            </div>
                          ) : (
                            <div>
                              <h3 className="text-base md:text-lg font-semibold" style={{ color: SECONDARY_COLOR }}>
                                {company?.name || 'Unnamed Company'}
                              </h3>
                              <div className="flex flex-wrap items-center gap-1 md:gap-2 mt-1">
                                <span className={`px-2 py-0.5 md:py-1 rounded-full text-xs ${getStatusColor(company?.status)}`}>
                                  {(company?.status || 'unknown').charAt(0).toUpperCase() + (company?.status || '').slice(1)}
                                </span>
                                <span className="text-xs md:text-sm text-gray-500">
                                  Registered: {formatDate(company?.createdAt)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between md:justify-end gap-2 md:gap-4">
                      <div className="text-right hidden sm:block">
                        {editingCompany !== company?._id ? (
                          <>
                            <div className="flex items-center gap-1 md:gap-2">
                              <span className="text-xs md:text-sm text-gray-500">Status:</span>
                              <span className={`text-xs md:text-sm font-medium ${company?.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                                {company?.status === 'active' ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5 md:mt-1">
                              Last updated: {formatDate(company?.updatedAt)}
                            </div>
                          </>
                        ) : null}
                      </div>
                      
                      <div className="flex items-center gap-1 md:gap-2">
                        {editingCompany === company?._id ? (
                          <>
                            <button
                              onClick={() => handleUpdateCompany(company?._id)}
                              disabled={isUpdating}
                              className="p-1.5 md:p-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ backgroundColor: PRIMARY_COLOR }}
                              onMouseEnter={(e) => !isUpdating && (e.target.style.backgroundColor = '#d1763a')}
                              onMouseLeave={(e) => !isUpdating && (e.target.style.backgroundColor = PRIMARY_COLOR)}
                            >
                              {isUpdating ? (
                                <div className="w-4 h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <CheckIcon className="w-4 h-4 md:w-5 md:h-5" />
                              )}
                            </button>
                            <button
                              onClick={handleEditCancel}
                              disabled={isUpdating}
                              className="p-1.5 md:p-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XMarkIcon className="w-4 h-4 md:w-5 md:h-5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditStart(company)}
                              className="p-1.5 md:p-2 hover:bg-blue-50 rounded-lg transition-colors"
                              style={{ color: PRIMARY_COLOR }}
                              title="Edit company"
                            >
                              <PencilIcon className="w-4 h-4 md:w-5 md:h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteCompany(company?._id, company?.name)}
                              className="p-1.5 md:p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600"
                              title="Delete company"
                            >
                              <TrashIcon className="w-4 h-4 md:w-5 md:h-5" />
                            </button>
                            <button
                              onClick={() => toggleExpand(company?._id)}
                              className="p-1.5 md:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              style={{ color: SECONDARY_COLOR }}
                            >
                              {expandedCompany === company?._id ? (
                                <ChevronUpIcon className="w-4 h-4 md:w-5 md:h-5" />
                              ) : (
                                <ChevronDownIcon className="w-4 h-4 md:w-5 md:h-5" />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Company Quick Info - Edit Mode */}
                  {editingCompany === company?._id ? (
                    <div className="mt-4 md:mt-6 space-y-3 md:space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={editForm.email}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm md:text-base focus:ring-2 focus:border-transparent"
                            style={{ focusRingColor: PRIMARY_COLOR }}
                            placeholder="Enter email address"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={editForm.phone}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm md:text-base focus:ring-2 focus:border-transparent"
                            style={{ focusRingColor: PRIMARY_COLOR }}
                            placeholder="Enter phone number"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            Registration Number
                          </label>
                          <input
                            type="text"
                            name="registrationNumber"
                            value={editForm.registrationNumber}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm md:text-base focus:ring-2 focus:border-transparent"
                            style={{ focusRingColor: PRIMARY_COLOR }}
                            placeholder="Enter registration number"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            Tax ID
                          </label>
                          <input
                            type="text"
                            name="taxId"
                            value={editForm.taxId}
                            onChange={handleEditChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm md:text-base focus:ring-2 focus:border-transparent"
                            style={{ focusRingColor: PRIMARY_COLOR }}
                            placeholder="Enter tax ID"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Company Quick Info - View Mode */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 mt-3 md:mt-4">
                        <div className="flex items-center gap-1 md:gap-2">
                          <EnvelopeIcon className="w-3 h-3 md:w-4 md:h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-xs md:text-sm text-gray-600 truncate">
                            {company?.email || 'No email'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 md:gap-2">
                          <PhoneIcon className="w-3 h-3 md:w-4 md:h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-xs md:text-sm text-gray-600 truncate">
                            {company?.phone || 'No phone'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 md:gap-2">
                          <MapPinIcon className="w-3 h-3 md:w-4 md:h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-xs md:text-sm text-gray-600 truncate">
                            {getAddressString(company?.address)}
                          </span>
                        </div>
                      </div>

                      {/* Company Stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-4 mt-3 md:mt-4">
                        <div className="bg-gray-50 p-2 md:p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500">Drivers</p>
                              <p className="text-base md:text-lg font-semibold" style={{ color: SECONDARY_COLOR }}>
                                {company?.drivers?.length || 0}
                              </p>
                            </div>
                            <UsersIcon className="w-4 h-4 md:w-5 md:h-5" style={{ color: PRIMARY_COLOR }} />
                          </div>
                        </div>

                        <div className="bg-gray-50 p-2 md:p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500">Vehicles</p>
                              <p className="text-base md:text-lg font-semibold" style={{ color: SECONDARY_COLOR }}>
                                {company?.vehicles?.length || 0}
                              </p>
                            </div>
                            <TruckIcon className="w-4 h-4 md:w-5 md:h-5" style={{ color: PRIMARY_COLOR }} />
                          </div>
                        </div>

                        <div className="bg-gray-50 p-2 md:p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500">Registration</p>
                              <p className="text-sm md:text-base font-semibold truncate" style={{ color: SECONDARY_COLOR }}>
                                {company?.registrationNumber || 'N/A'}
                              </p>
                            </div>
                            <DocumentTextIcon className="w-4 h-4 md:w-5 md:h-5" style={{ color: PRIMARY_COLOR }} />
                          </div>
                        </div>

                        <div className="bg-gray-50 p-2 md:p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500">Tax ID</p>
                              <p className="text-sm md:text-base font-semibold truncate" style={{ color: SECONDARY_COLOR }}>
                                {company?.taxId || 'N/A'}
                              </p>
                            </div>
                            <DocumentTextIcon className="w-4 h-4 md:w-5 md:h-5" style={{ color: PRIMARY_COLOR }} />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Expanded Details (Only in view mode) */}
                  {expandedCompany === company?._id && editingCompany !== company?._id && (
                    <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-200">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                        {/* Drivers Section */}
                        <div>
                          <h4 className="font-medium mb-3 md:mb-4 flex items-center gap-1 md:gap-2" style={{ color: SECONDARY_COLOR }}>
                            <UsersIcon className="w-4 h-4 md:w-5 md:h-5" />
                            Drivers ({company?.drivers?.length || 0})
                          </h4>
                          {(company?.drivers?.length || 0) === 0 ? (
                            <p className="text-gray-500 text-xs md:text-sm">No drivers registered</p>
                          ) : (
                            <div className="space-y-2 md:space-y-3">
                              {(company?.drivers || []).map((driver) => (
                                <div key={driver?._id || Math.random()} className="bg-gray-50 p-2 md:p-3 rounded-lg">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 md:gap-2">
                                    <div className="flex-1">
                                      <p className="font-medium text-sm md:text-base">{driver?.driverId || 'No ID'}</p>
                                      <p className="text-xs md:text-sm text-gray-600 truncate">{driver?.licenseNumber || 'No license'}</p>
                                      <div className="flex items-center gap-1 md:gap-2 mt-1">
                                        <span className={`px-2 py-0.5 md:py-1 rounded-full text-xs ${getDriverStatusColor(driver?.status)}`}>
                                          {driver?.status || 'unknown'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          Exp: {driver?.experience || 0} yrs
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-right mt-1 sm:mt-0">
                                      <p className="text-xs md:text-sm text-gray-600 truncate">
                                        {driver?.availability || 'N/A'}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        Last check-in: {formatDate(driver?.lastCheckIn)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Vehicles Section */}
                        <div>
                          <h4 className="font-medium mb-3 md:mb-4 flex items-center gap-1 md:gap-2" style={{ color: SECONDARY_COLOR }}>
                            <TruckIcon className="w-4 h-4 md:w-5 md:h-5" />
                            Vehicles ({company?.vehicles?.length || 0})
                          </h4>
                          {(company?.vehicles?.length || 0) === 0 ? (
                            <p className="text-gray-500 text-xs md:text-sm">No vehicles registered</p>
                          ) : (
                            <div className="space-y-2 md:space-y-3">
                              {(company?.vehicles || []).map((vehicle) => (
                                <div key={vehicle?._id || Math.random()} className="bg-gray-50 p-2 md:p-3 rounded-lg">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 md:gap-2">
                                    <div className="flex-1">
                                      <p className="font-medium text-sm md:text-base">{vehicle?.registration || 'No registration'}</p>
                                      <p className="text-xs md:text-sm text-gray-600 truncate">{vehicle?.model || 'No model'} ({vehicle?.year || 'N/A'})</p>
                                      <div className="flex items-center gap-1 md:gap-2 mt-1">
                                        <span className={`px-2 py-0.5 md:py-1 rounded-full text-xs ${getVehicleStatusColor(vehicle?.status)}`}>
                                          {vehicle?.status || 'unknown'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {vehicle?.specifications?.fuelType || 'N/A'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-right mt-1 sm:mt-0">
                                      <p className="text-xs md:text-sm text-gray-600">
                                        Mileage: {(vehicle?.mileage || 0).toLocaleString()}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        Fuel: {vehicle?.fuelLevel || 0}%
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Company Details */}
                      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-200">
                        <h4 className="font-medium mb-3 md:mb-4" style={{ color: SECONDARY_COLOR }}>Company Details</h4>
                        <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                            <div>
                              <p className="text-xs md:text-sm text-gray-500">Company ID</p>
                              <p className="font-medium text-xs md:text-sm truncate" style={{ color: SECONDARY_COLOR }}>
                                {company?._id || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs md:text-sm text-gray-500">Created</p>
                              <p className="font-medium text-xs md:text-sm" style={{ color: SECONDARY_COLOR }}>
                                {formatDate(company?.createdAt)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs md:text-sm text-gray-500">Updated</p>
                              <p className="font-medium text-xs md:text-sm" style={{ color: SECONDARY_COLOR }}>
                                {formatDate(company?.updatedAt)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs md:text-sm text-gray-500">Admin Email</p>
                              <p className="font-medium text-xs md:text-sm truncate" style={{ color: SECONDARY_COLOR }}>
                                {company?.admin?.email || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Companies;